import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDatepickerKeyboardService
} from '@ng-bootstrap/ng-bootstrap';
import { DatetimeService } from '../../../../services/datetime/datetime.service';
import { CustomAdapter } from '../../../../services/NgbDatepicker/custom-adapter.service';
import { CustomDateParserFormatter } from '../../../../services/NgbDatepicker/custom-dateparser-formatter.service';
import { CustomDatepickerI18n } from '../../../../services/NgbDatepicker/custom-datepicker-i18n.service';
import { CustomKeyboardService } from '../../../../services/NgbDatepicker/custom-keyboard-service';

export enum DashboardPeriod {
  Today = 'today',
  Yesterday = 'yesterday',
  ThisWeek = 'this-week',
  PreviousWeek = 'previous-week',
  Last7Days = 'last-7-days',
  ThisMonth = 'this-month',
  PreviousMonth = 'previous-month',
  Last30Days = 'last-30-days',
  ThisQuarter = 'this-quarter',
  PreviousQuarter = 'previous-quarter',
  ThisYear = 'this-year',
  PreviousYear = 'previous-year',
  Custom = 'custom'
}

export interface DashboardDateRange {
  period: DashboardPeriod;
  fromDMY: string;
  toDMY: string;
  fromYMD: string;
  toYMD: string;
  fromUnixTime: number;
  toUnixTime: number;
}

export const DEFAULT_DASHBOARD_PERIODS: DashboardPeriod[] = [
  DashboardPeriod.Today,
  DashboardPeriod.Yesterday,
  DashboardPeriod.ThisWeek,
  DashboardPeriod.PreviousWeek,
  DashboardPeriod.Last7Days,
  DashboardPeriod.ThisMonth,
  DashboardPeriod.PreviousMonth,
  DashboardPeriod.Last30Days,
  DashboardPeriod.ThisQuarter,
  DashboardPeriod.PreviousQuarter,
  DashboardPeriod.ThisYear,
  DashboardPeriod.PreviousYear,
  DashboardPeriod.Custom
];

@Component({
  selector: 'jbm-dashboard-date-range-filter',
  templateUrl: './dashboard-date-range-filter.component.html',
  styleUrls: ['./dashboard-date-range-filter.component.scss'],
  providers: [
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    { provide: NgbDatepickerKeyboardService, useClass: CustomKeyboardService }
  ]
})
export class JBMDashboardDateRangeFilterComponent implements OnInit {
  @Input() id: string = 'dashboard-date-range-filter';
  @Input() period: DashboardPeriod = DashboardPeriod.Today;
  @Input() fromDate: string = '';
  @Input() toDate: string = '';
  @Input() periods: DashboardPeriod[] = [...DEFAULT_DASHBOARD_PERIODS];
  @Input() disabled: boolean = false;
  @Input() sm: boolean = true;
  @Input() class: string = '';

  @Output() rangeChange = new EventEmitter<DashboardDateRange>();

  readonly DashboardPeriod = DashboardPeriod;

  constructor(private DatetimeService: DatetimeService) { }

  ngOnInit(): void {
    if(this.period === DashboardPeriod.Custom && this.rangeValid())
      Promise.resolve().then(() => this.emitRange());
    else {
      if(this.period === DashboardPeriod.Custom)
        this.period = DashboardPeriod.Today;
      this.setPeriod(this.period, false);
      Promise.resolve().then(() => this.emitRange());
    }
  }

  get periodOptions(): Array<{ key: DashboardPeriod; value: string }> {
    return this.periods.map(period => ({
      key: period,
      value: this.getPeriodLabel(period)
    }));
  }

  periodChange(period: DashboardPeriod): void {
    if(this.disabled || !this.periods.includes(period))
      return;

    this.setPeriod(period);
  }

  dateChange(): void {
    if(this.disabled)
      return;

    this.period = DashboardPeriod.Custom;
    if(this.rangeValid())
      this.emitRange();
  }

  fromIsValid(): boolean {
    return this.fromDate === '' || this.DatetimeService.validateDateStr(this.fromDate);
  }

  toIsValid(): boolean {
    return this.toDate === '' || this.DatetimeService.validateDateStr(this.toDate);
  }

  rangeValid(): boolean {
    if(!this.fromDate || !this.toDate || !this.fromIsValid() || !this.toIsValid())
      return false;

    return this.DatetimeService.dateDMYToYMD(this.fromDate) <=
      this.DatetimeService.dateDMYToYMD(this.toDate);
  }

  private setPeriod(period: DashboardPeriod, emit: boolean = true): void {
    this.period = period;
    if(period === DashboardPeriod.Custom) {
      if(this.rangeValid() && emit)
        this.emitRange();
      return;
    }

    const today = this.startOfDay(new Date());
    let from = new Date(today);
    let to = new Date(today);

    switch(period) {
      case DashboardPeriod.Yesterday:
        from = this.addDays(today, -1);
        to = new Date(from);
        break;
      case DashboardPeriod.ThisWeek:
        from = this.startOfWeek(today);
        to = this.addDays(from, 6);
        break;
      case DashboardPeriod.PreviousWeek:
        from = this.addDays(this.startOfWeek(today), -7);
        to = this.addDays(from, 6);
        break;
      case DashboardPeriod.Last7Days:
        from = this.addDays(today, -6);
        break;
      case DashboardPeriod.ThisMonth:
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case DashboardPeriod.PreviousMonth:
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case DashboardPeriod.Last30Days:
        from = this.addDays(today, -29);
        break;
      case DashboardPeriod.ThisQuarter: {
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        from = new Date(today.getFullYear(), quarterStartMonth, 1);
        to = new Date(today.getFullYear(), quarterStartMonth + 3, 0);
        break;
      }
      case DashboardPeriod.PreviousQuarter: {
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3 - 3;
        from = new Date(today.getFullYear(), quarterStartMonth, 1);
        to = new Date(today.getFullYear(), quarterStartMonth + 3, 0);
        break;
      }
      case DashboardPeriod.ThisYear:
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 11, 31);
        break;
      case DashboardPeriod.PreviousYear:
        from = new Date(today.getFullYear() - 1, 0, 1);
        to = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case DashboardPeriod.Today:
      default:
        break;
    }

    this.fromDate = this.DatetimeService.getDateToDMY(from);
    this.toDate = this.DatetimeService.getDateToDMY(to);
    if(emit)
      this.emitRange();
  }

  private emitRange(): void {
    if(!this.rangeValid())
      return;

    const fromParts = this.DatetimeService.getDatePartsDMY(this.fromDate);
    const toParts = this.DatetimeService.getDatePartsDMY(this.toDate);
    const fromDate = new Date(fromParts.year, fromParts.month - 1, fromParts.day, 0, 0, 0, 0);
    const toDate = new Date(toParts.year, toParts.month - 1, toParts.day, 23, 59, 59, 999);

    this.rangeChange.emit({
      period: this.period,
      fromDMY: this.fromDate,
      toDMY: this.toDate,
      fromYMD: this.DatetimeService.dateDMYToYMD(this.fromDate),
      toYMD: this.DatetimeService.dateDMYToYMD(this.toDate),
      fromUnixTime: fromDate.getTime(),
      toUnixTime: toDate.getTime()
    });
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private startOfWeek(date: Date): Date {
    const mondayOffset = (date.getDay() + 6) % 7;
    return this.addDays(date, -mondayOffset);
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private getPeriodLabel(period: DashboardPeriod): string {
    switch (period) {
      case DashboardPeriod.Today: return 'ui.calendar-today';
      case DashboardPeriod.Yesterday: return 'ui.calendar-yesterday';
      case DashboardPeriod.ThisWeek: return 'ui.calendar-this-week';
      case DashboardPeriod.PreviousWeek: return 'ui.calendar-previous-week';
      case DashboardPeriod.Last7Days: return 'ui.calendar-last-7-days';
      case DashboardPeriod.ThisMonth: return 'ui.calendar-this-month';
      case DashboardPeriod.PreviousMonth: return 'ui.calendar-previous-month';
      case DashboardPeriod.Last30Days: return 'ui.calendar-last-30-days';
      case DashboardPeriod.ThisQuarter: return 'ui.calendar-this-quarter';
      case DashboardPeriod.PreviousQuarter: return 'ui.calendar-previous-quarter';
      case DashboardPeriod.ThisYear: return 'ui.calendar-this-year';
      case DashboardPeriod.PreviousYear: return 'ui.calendar-previous-year';
      case DashboardPeriod.Custom: return 'ui.calendar-custom';
    }
  }
}
